import React, { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import List from '#/common/components/List';
import type { Model } from '#/graphql/types';
import { getModelList } from '#/graphql/queries';
import columns from '../ModelDataGrid/columns';
import { Filter } from '#/common/components/DataGrid';
import useModelDelete from '../../hooks/useDeleteModelMutation';
import useToast from '#/common/hooks/useToast';
import { List as DxList } from 'devextreme-react/list';
import { inUseStatuses } from '../../utils/constants';

const getModelListQuery = gql(getModelList);

export interface ModelListProps {
  permanentFilters?: Filter[];
}

const ModelList: React.FC<ModelListProps> = (props) => {
  const { permanentFilters } = props;

  const navigate = useNavigate();

  const list = useRef<DxList | null>(null);
  const { confirm, alert } = useToast();
  const { deleteModel } = useModelDelete();

  const deleteItem = useCallback(
    async (items?: Model[], clearSelectedItems?: () => void) => {
      const filteredItemsForDelete = items?.filter(
        (model) =>
          model.inUseStatus === inUseStatuses.newlyCreated.key &&
          !(model.thirdPartyId && model.thirdPartySource),
      );

      if (!items?.length || !filteredItemsForDelete?.length) {
        alert('warning', 'No deletable models found. Only "Newly Created" ones can be deleted.');
        return undefined;
      }

      if (filteredItemsForDelete.length < items.length) {
        const sure = await confirm(
          'error',
          `Some selected models are not in 'Newly Created' status and can not be deleted.`,
        );
        if (!sure) {
          return;
        }
      } else {
        const sure = await confirm('error', `Are you sure you want to delete these models?`);
        if (!sure) {
          return;
        }
      }

      await deleteModel(items.map((item) => item.id).join(','));
      list.current && list.current.instance.reload();
      clearSelectedItems && clearSelectedItems();
    },
    [deleteModel],
  );

  const openModel = useCallback(
    (model: Model) => {
      navigate(`/assets/models/${model.id}`);
    },
    [navigate],
  );

  return (
    <List<Model>
      setRef={(ref) => (list.current = ref)}
      titleKey="name"
      subtitleKey="oemPartNumber"
      onClick={openModel}
      getItemsQuery={getModelListQuery}
      permanentFilters={permanentFilters}
      columnList={columns}
      bodyFields={[
        { key: 'specification', label: 'Specification' },
        { key: 'manufacturer.name', label: 'Manufacturer' },
      ]}
      isActionDeleteOnly
      floatingButtonOnClick={deleteItem}
    />
  );
};

export default React.memo(ModelList);
